import { useEffect } from 'react';
import { useFetcher } from 'react-router';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { getImageAttrs } from '~/modules/images';
import type { ImageColumnProps } from '@modules/page-builder/types';
import type { PageBuilderGetModuleProductAction } from '~/routes/remix-api.page-builder.get-module-product';
import { QUERY_MODULE_PRODUCT } from '~/routes/remix-api.page-builder.get-module-product';
import { RevampSectionHeading } from './RevampSectionHeading';
import { REVAMP_TILE_SIZES } from '../../util/revamp-image-sizes';

/**
 * One product tile (Figma "Tile / Product" 1679:75723): the cut-out photo on
 * the warm grey swatch, then a truncated title and supplier line. Product
 * name/supplier/link resolve through the same deferred lookup the legacy
 * gallery uses; the column caption fills in until it lands.
 */
const CollectionTile = ({ column }: { column: ImageColumnProps }) => {
    const fetcher = useFetcher<PageBuilderGetModuleProductAction>();

    useEffect(() => {
        if (
            column.productId &&
            fetcher.state === 'idle' &&
            fetcher.data === undefined
        ) {
            fetcher.load(
                `${QUERY_MODULE_PRODUCT}?productId=${encodeURIComponent(column.productId)}`,
            );
        }
    }, [fetcher, column.productId]);

    const product = fetcher.data;
    const title = product?.productName || column.caption || '';

    const tile = (
        <>
            <div className="relative aspect-[437/524] w-full overflow-hidden rounded-tile bg-[#f9f6f6]">
                {column.imageUrl && (
                    <img
                        {...getImageAttrs(column.imageUrl, {
                            sizes: REVAMP_TILE_SIZES,
                        })}
                        alt={title}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 size-full object-contain mix-blend-multiply"
                    />
                )}
            </div>
            <div className="flex min-w-0 flex-col leading-[1.3]">
                {title && (
                    <p className="m-0 w-full truncate font-sans font-medium text-[#181818] max-md:text-base md:text-[1.375rem]">
                        {title}
                    </p>
                )}
                {product?.professionalName && (
                    <p className="m-0 w-full truncate font-sans font-normal text-[#767676] max-md:text-sm md:text-[1.375rem]">
                        {product.professionalName}
                    </p>
                )}
            </div>
        </>
    );

    const tileClass = 'flex min-w-0 flex-col gap-2 md:gap-[0.625rem]';

    return product?.productLink ? (
        <RoccoLink
            to={product.productLink}
            className={cn(tileClass, 'transition-opacity hover:opacity-80')}
        >
            {tile}
        </RoccoLink>
    ) : (
        <div className={tileClass}>{tile}</div>
    );
};

interface RevampCollectionModuleProps {
    columns: ImageColumnProps[];
    /** Optional ruled heading, from the module's h3 title column. */
    titleHTML?: string;
}

/**
 * "Single row collection" (Figma 2479:70454): a row of product tiles under the
 * module's optional ruled heading. Only the titled presets carry a heading
 * column, so a collection built from the plain gallery presets renders as a
 * bare grid.
 */
export const RevampCollectionModule = ({
    columns,
    titleHTML,
}: RevampCollectionModuleProps) => {
    const products = columns.filter((column) => column.imageUrl);
    if (products.length === 0) return null;

    return (
        <section className="w-full">
            <RevampSectionHeading contentHTML={titleHTML} />
            <div
                className={cn(
                    'grid grid-cols-2 gap-x-4 gap-y-10 px-4.5 md:grid-cols-4 md:gap-6 md:px-[3rem]',
                    titleHTML && 'mt-6 md:mt-[1.5rem]',
                )}
            >
                {products.map((column) => (
                    <CollectionTile key={column.id} column={column} />
                ))}
            </div>
        </section>
    );
};
